import isArray from 'lodash/isArray'
import isEmpty from 'lodash/isEmpty'

import { WidgetDataSourceKey } from 'const/dataSources'
import { DATES } from 'const/filters'
import { DateRangeWithLagUtil } from 'features/Filters/components/BaseFilterControls/DateRangeControl/DateRangeWithLagUtil'
import { hoursToWeeklyDataBoundary } from 'helpers/dateRange'
import { FilterValue, Widget } from 'types'

export { derivesRangeLag } from './derivedLagPages'

const hasOwnDates = (widget: Widget): boolean =>
    !!widget.filters?.[DATES] && !isEmpty(widget.filters[DATES])

/** Whether a widget takes its tab's date range, having none of its own. */
export const followsTab = (widget: Widget): boolean => !hasOwnDates(widget)

const showsShelfSearchVolume = (widget: Widget): boolean =>
    widget.data_source === 'sov_search_results' &&
    widget.metrics.some((metric) => metric.id === 'estimated_search_volume')

/**
 * Share-of-voice search results with estimated search volume read weekly data, so
 * a range that covers one ends its presets on the weekly boundary rather than a
 * source's daily lag.
 */
export function shelfSearchVolumeLag(
    widgets: Widget[] | undefined
): number | undefined {
    return widgets?.some(showsShelfSearchVolume)
        ? hoursToWeeklyDataBoundary()
        : undefined
}

/**
 * How many hours back a date range ends for these sources: the slowest one's
 * delay, for the presets that take a delay, and none for a custom range or a
 * preset such as Today that means what it says.
 */
export function rangeLagFor(
    dates: FilterValue | undefined,
    dataSources: WidgetDataSourceKey[],
    customRangeLag?: number
): number {
    if (
        !dates ||
        isArray(dates) ||
        !DateRangeWithLagUtil.includePreset.includes(dates as string)
    ) {
        return 0
    }
    if (customRangeLag !== undefined) {
        return customRangeLag
    }
    return (
        DateRangeWithLagUtil.getDatasourceWithBiggestLag(dataSources)?.lag ?? 0
    )
}

/** The widgets that follow the tab's date range: those without their own. */
export function tabFollowers(widgets: Widget[] | undefined): Widget[] {
    return (widgets ?? []).filter(followsTab)
}

/** The data sources that follow the tab's date range. */
export function tabDataSources(
    widgets: Widget[] | undefined
): WidgetDataSourceKey[] {
    return tabFollowers(widgets).map((widget) => widget.data_source)
}

/** The lag of a tab's own date range, shared by every widget that follows it. */
export function tabRangeLag(
    widgets: Widget[] | undefined,
    dates: FilterValue | undefined
): number {
    return rangeLagFor(
        dates,
        tabDataSources(widgets),
        shelfSearchVolumeLag(tabFollowers(widgets))
    )
}

/**
 * The lag one widget queries with. A widget with its own date range answers for
 * its own source; one that follows the tab shares the tab's, so every widget on
 * the tab ends on the same day. `dates` is the range the widget resolved to.
 */
export function widgetRangeLag(
    widget: Widget,
    tabWidgets: Widget[] | undefined,
    dates: FilterValue | undefined
): number {
    if (hasOwnDates(widget)) {
        return rangeLagFor(
            dates,
            [widget.data_source],
            shelfSearchVolumeLag([widget])
        )
    }
    return tabRangeLag(tabWidgets ?? [widget], dates)
}
